import { useEffect } from "react";
import { Link } from "react-router-dom";
import { usePlatform } from "@/context/PlatformContext";
import { JasrMark } from "@/components/JasrLogo";
import { FileText, AlertTriangle, Users, Ban, ChevronLeft, Mail } from "lucide-react";

const SECTIONS = [
  {
    id: "service",
    icon: FileText,
    color: "#3B82F6",
    title: "وصف الخدمة",
    content: [
      "منصة جسر هي خدمة SaaS تتيح لتجار سلة مزامنة مخزونهم مع نظام قيود للمحاسبة بشكل تلقائي.",
      "تشمل الخدمة: مزامنة المخزون، لوحة التحليلات، أداة تحسين السرعة، أداة تحسين SEO، استيراد Excel، وإدارة الفروع.",
      "الخدمة متاحة للتجار المسجلين في منصة سلة للتجارة الإلكترونية.",
      "نحتفظ بحق تعديل الخدمة أو إيقافها مؤقتاً لأغراض الصيانة مع إشعار مسبق.",
    ],
  },
  {
    id: "obligations",
    icon: Users,
    color: "#10B981",
    title: "التزامات المستخدم",
    content: [
      "تقديم معلومات صحيحة ودقيقة عند التسجيل والاستخدام.",
      "الحفاظ على سرية بيانات تسجيل الدخول (البريد الإلكتروني وكلمة المرور).",
      "إخطارنا فوراً في حال الاشتباه باختراق حسابك.",
      "عدم مشاركة حسابك مع أطراف غير مصرّح لها.",
      "استخدام الخدمة بما يتوافق مع سياسات سلة وقيود وهذه الشروط.",
      "أنت تتحمل المسؤولية الكاملة عن أي عمليات تُجرى من خلال حسابك.",
    ],
  },
  {
    id: "prohibited",
    icon: Ban,
    color: "#EF4444",
    title: "الاستخدامات المحظورة",
    content: [
      "محاولة اختراق أو تعطيل أنظمة المنصة أو خوادمها.",
      "استخدام الخدمة لأنشطة غير قانونية أو احتيالية.",
      "محاولة الوصول إلى بيانات مستخدمين آخرين.",
      "إعادة بيع الخدمة أو استخدامها تجارياً دون إذن كتابي مسبق.",
      "رفع أو نقل محتوى ينتهك حقوق الملكية الفكرية.",
      "استخدام برامج آلية للوصول المفرط (Scraping/Bots) دون إذن.",
    ],
  },
  {
    id: "liability",
    icon: AlertTriangle,
    color: "#F59E0B",
    title: "حدود المسؤولية",
    content: [
      "الخدمة مقدّمة **كما هي** — نسعى لتوفير أعلى مستوى جودة لكن لا نضمن الخلو التام من الأخطاء.",
      "لسنا مسؤولين عن أي خسائر ناتجة عن أخطاء في بيانات قيود أو سلة (مصدر البيانات).",
      "لسنا مسؤولين عن الأضرار غير المباشرة أو الخسائر التجارية الناجمة عن توقف الخدمة.",
      "الحد الأقصى لمسؤوليتنا لا يتجاوز المبلغ الذي دفعته مقابل الخدمة خلال الشهر السابق.",
      "في حالات القوة القاهرة (انقطاع الإنترنت، كوارث طبيعية، إلخ) لا نتحمل أي مسؤولية.",
    ],
  },
  {
    id: "ip",
    icon: FileText,
    color: "#A78BFA",
    title: "الملكية الفكرية",
    content: [
      "جميع محتويات المنصة (الكود، التصميم، الشعارات، الوثائق) مملوكة لفريق جسر.",
      "بالتسجيل، تمنحنا رخصة محدودة لاستخدام بياناتك لتشغيل الخدمة فقط.",
      "لا يحق لك نسخ أو توزيع أي جزء من المنصة دون إذن كتابي مسبق.",
      "شعار سلة وشعار قيود ملكية لأصحابها المعنيين.",
    ],
  },
  {
    id: "termination",
    icon: Ban,
    color: "#6366F1",
    title: "إنهاء الحساب",
    content: [
      "يمكنك إنهاء اشتراكك في أي وقت من إعدادات الحساب.",
      "نحتفظ بالحق في تعليق أو إنهاء أي حساب يُخالف هذه الشروط دون إشعار مسبق.",
      "عند إنهاء الحساب، سيتم حذف بياناتك خلال 30 يوماً.",
      "الأرصدة المدفوعة عن فترات لم تُستخدم تخضع لسياسة الاسترداد المعمول بها.",
    ],
  },
];

export default function TermsPage() {
  const { name: platformName, logo_base64 } = usePlatform();

  useEffect(() => {
    document.title = `شروط الاستخدام — ${platformName || "جسر"}`;
    window.scrollTo(0, 0);
  }, [platformName]);

  return (
    <div dir="rtl" className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Tajawal', sans-serif" }}>

      {/* Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {logo_base64
              ? <img src={logo_base64} alt={platformName} className="w-7 h-7 rounded-lg object-contain"/>
              : <JasrMark className="w-7 h-7"/>}
            <span className="font-bold text-slate-900">{platformName || "جسر"}</span>
          </Link>
          <Link to="/" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#004EEB] transition-colors">
            <ChevronLeft className="w-4 h-4"/>
            العودة للرئيسية
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-[#030B1A] py-14 sm:py-20 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(rgba(0,78,235,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,78,235,0.04) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}/>
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-5 text-xs font-bold"
            style={{ background: 'rgba(0,78,235,0.15)', border: '1px solid rgba(0,78,235,0.3)', color: '#93C5FD' }}>
            <FileText className="w-3.5 h-3.5"/> قانوني
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-white mb-4">شروط الاستخدام</h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto">
            يُرجى قراءة هذه الشروط بعناية قبل استخدام منصة جسر.
          </p>
          <p className="text-slate-500 text-sm mt-4">آخر تحديث: فبراير 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">

        {/* Intro */}
        <div className="bg-white border border-slate-200 rounded-2xl p-7 mb-8 shadow-sm">
          <p className="text-slate-600 leading-relaxed text-base">
            باستخدامك لمنصة <strong className="text-slate-900">{platformName || "جسر"}</strong>، فأنت توافق على الالتزام بهذه الشروط والأحكام.
            إذا كنت لا توافق على أي من هذه الشروط، يُرجى التوقف عن استخدام الخدمة.
            هذه الشروط تُطبَّق على جميع المستخدمين بما فيهم الزوار والمشتركين.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {SECTIONS.map((sec) => (
            <div key={sec.id} className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm hover:border-[#004EEB]/20 hover:shadow-md transition-all">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${sec.color}15` }}>
                  <sec.icon className="w-5 h-5" style={{ color: sec.color }} strokeWidth={1.5}/>
                </div>
                <h2 className="text-xl font-bold text-slate-900">{sec.title}</h2>
              </div>
              <ul className="space-y-3">
                {sec.content.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0 mt-2"/>
                    <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-800">$1</strong>') }}/>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Governing law */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
            <h2 className="text-xl font-bold text-slate-900 mb-4">القانون المطبّق</h2>
            <p className="text-slate-600 text-sm leading-relaxed">
              تخضع هذه الشروط وتُفسَّر وفقاً لأنظمة المملكة العربية السعودية. أي نزاع ينشأ عن هذه الشروط يُحسم أمام الجهات القضائية المختصة في المملكة العربية السعودية.
            </p>
          </div>

          {/* Contact */}
          <div className="rounded-2xl p-7" style={{ background: 'linear-gradient(135deg, #004EEB 0%, #0038CC 100%)' }}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-white" strokeWidth={1.5}/>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-2">تواصل معنا</h2>
                <p className="text-blue-100 text-sm leading-relaxed mb-4">
                  لأي استفسار حول هذه الشروط أو الخدمة، تواصل مع فريق الدعم:
                </p>
                <a href="mailto:support@getjisr.com"
                  className="inline-flex items-center gap-2 bg-white text-[#004EEB] px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-blue-50 transition-colors">
                  <Mail className="w-4 h-4"/>
                  support@getjisr.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="text-center mt-12 pt-8 border-t border-slate-200">
          <p className="text-slate-400 text-sm">
            نحتفظ بحق تعديل هذه الشروط. سنُعلمك بالتغييرات الجوهرية قبل 30 يوماً عبر البريد الإلكتروني.
          </p>
          <div className="flex items-center justify-center gap-4 mt-5">
            <Link to="/" className="text-sm text-[#004EEB] hover:underline">الصفحة الرئيسية</Link>
            <span className="text-slate-300">·</span>
            <Link to="/privacy" className="text-sm text-[#004EEB] hover:underline">سياسة الخصوصية</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
